/**
 * Assessment Page
 *
 * Main HVAC assessment wizard - 5-step process with results
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAssessment } from '../hooks/useAssessment';
import { loadGoogleMapsScript } from '../utils/loadGoogleMaps';
import ProgressIndicator from '../components/assessment/ProgressIndicator';
import Step1CustomerInfo from '../components/assessment/Step1CustomerInfo';
import Step2Location from '../components/assessment/Step2Location';
import Step3SystemInfo from '../components/assessment/Step3SystemInfo';
import Step4Technical from '../components/assessment/Step4Technical';
import Step5Comfort from '../components/assessment/Step5Comfort';
import Results from '../components/assessment/Results';

export default function Assessment() {
  const { hasActiveSubscription } = useAuth();
  const {
    state,
    currentStep,
    errors,
    results,
    isCalculating,
    isSaving,
    isGeneratingAI,
    assessmentId,
    aiSummary,
    canProceed,
    setField,
    setEnvelopeField,
    toggleIssue,
    nextStep,
    prevStep,
    goToStep,
    updateEnergyCostEstimate,
    calculateResults,
    saveAssessment,
    generateAISummary,
    reset
  } = useAssessment();

  // Load Google Maps API for address autocomplete in Step 1
  useEffect(() => {
    loadGoogleMapsScript().catch(err => {
      console.error('Failed to load Google Maps:', err);
    });
  }, []);

  const handleNext = () => {
    if (currentStep === 5) {
      // On final step (comfort), calculate results
      calculateResults();
    } else {
      nextStep();
    }
  };

  // Check if subscription is active
  if (!hasActiveSubscription()) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ maxWidth: '600px', width: '100%' }}>
          <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', marginBottom: '12px', color: '#2c3e50' }}>
              Subscription Required
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '30px' }}>
              Your trial has expired or your subscription is inactive. Please update your billing information to continue creating assessments.
            </p>
            <div className="mobile-stack" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/settings" className="btn btn-primary mobile-full-width">
                Update Billing
              </Link>
              <Link to="/dashboard" className="btn btn-secondary mobile-full-width">
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '20px 12px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          <h1 style={{
            margin: '0 0 10px 0',
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            HVAC System Assessment
          </h1>
          <p style={{
            margin: '0',
            color: '#64748b',
            fontSize: '16px'
          }}>
            Professional analysis in 6 simple steps
          </p>
        </div>

        {/* Progress Indicator */}
        {!results && (
          <ProgressIndicator
            currentStep={currentStep}
            onStepClick={goToStep}
          />
        )}

        {/* Error Display */}
        {errors.general && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            <strong>Error:</strong> {errors.general}
          </div>
        )}

        {/* Step Content */}
        {!results && (
          <>
            {currentStep === 1 && (
              <Step1CustomerInfo
                state={state}
                setField={setField}
                errors={errors}
              />
            )}

            {currentStep === 2 && (
              <Step2Location
                state={state}
                setField={setField}
                errors={errors}
              />
            )}

            {currentStep === 3 && (
              <Step3SystemInfo
                state={state}
                setField={setField}
                errors={errors}
              />
            )}

            {currentStep === 4 && (
              <Step4Technical
                state={state}
                setField={setField}
                setEnvelopeField={setEnvelopeField}
                errors={errors}
              />
            )}

            {currentStep === 5 && (
              <Step5Comfort
                state={state}
                toggleIssue={toggleIssue}
                setField={setField}
                updateEnergyCostEstimate={updateEnergyCostEstimate}
                errors={errors}
              />
            )}

            {/* Navigation Buttons */}
            <div className="mobile-stack" style={{
              display: 'flex',
              gap: '12px',
              marginTop: '20px',
              justifyContent: 'space-between'
            }}>
              {/* Back Button */}
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="mobile-full-width"
                style={{
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: '2px solid #e0e6ed',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentStep === 1 ? 0.5 : 1,
                  transition: 'all 0.2s',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  if (currentStep !== 1) {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentStep !== 1) {
                    e.target.style.borderColor = '#e0e6ed';
                    e.target.style.color = '#64748b';
                  }
                }}
              >
                ← Back
              </button>

              {/* Next/Calculate Button */}
              <button
                onClick={handleNext}
                disabled={!canProceed || isCalculating}
                className="mobile-full-width"
                style={{
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: canProceed && !isCalculating
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#cbd5e1',
                  color: 'white',
                  cursor: canProceed && !isCalculating ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  minWidth: '180px',
                  boxShadow: canProceed && !isCalculating ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (canProceed && !isCalculating) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (canProceed && !isCalculating) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                  }
                }}
              >
                {isCalculating ? (
                  '⏳ Calculating...'
                ) : currentStep === 6 ? (
                  '🔍 Calculate Results'
                ) : (
                  'Next →'
                )}
              </button>
            </div>
          </>
        )}

        {/* Results Display */}
        {results && (
          <Results
            results={results}
            state={state}
            onReset={reset}
            onSave={saveAssessment}
            isSaving={isSaving}
            assessmentId={assessmentId}
            aiSummary={aiSummary}
            isGeneratingAI={isGeneratingAI}
            aiError={errors.ai}
            onGenerateAI={generateAISummary}
          />
        )}
      </div>
    </div>
  );
}
